import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef, memo, useMemo } from 'react';
import {
  LogOut,
  Home,
  Box,
  Users as UsersIcon,
  Calendar,
  Building,
  ScanLine,
  Shield,
  ClipboardList,
  Menu,
  Sun,
  Moon,
  ChevronRight,
  ChevronLeft,
  UserCog,
  Ticket,
  Tickets,
  Settings,
  Wallet,
  CreditCard,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { useEmpresa } from '@/components/hooks/useEmpresa';
import { useProfiles } from '@/components/hooks/useProfiles';
import { logoutUser } from '@/components/utils/logoutUser';
import { useTheme } from '@/components/hooks/useTheme';
import { guardNavigate } from '@/lib/navigationGuard';
import NotificationBell from '@/components/ui/NotificationBell';
import HelpButton from '@/components/ui/HelpButton';

const containerVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration: 0.3 } },
};

const submenuVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: 'auto', opacity: 1, transition: { duration: 0.25 } },
  exit: { height: 0, opacity: 0, transition: { duration: 0.2 } },
};

const chevronVariants = {
  closed: { rotate: 0 },
  open: { rotate: 90, transition: { duration: 0.25 } },
};

function useScrollProgress(resetKey: string, paused: boolean, threshold = 6) {
  const [progress, setProgress] = useState(0);
  const pausedRef = useRef(paused);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    setProgress(0);
  }, [resetKey]);

  useEffect(() => {
    let lastY = 0;
    let raf = 0;
    const onScroll = (e: Event) => {
      // Ignora el scroll interno del menú móvil (u otros overlays) mientras está abierto,
      // para que no "contamine" el progreso de la tab bar de fondo.
      if (pausedRef.current) return;

      const target = e.target as HTMLElement | Document;
      const y =
        target === document || target === document.documentElement
          ? window.scrollY
          : (target as HTMLElement).scrollTop ?? 0;

      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (y < 20) {
          setProgress(0);
          lastY = y;
          return;
        }
        const delta = y - lastY;
        if (Math.abs(delta) < threshold) return;
        setProgress(delta > 0 ? 1 : 0);
        lastY = y;
      });
    };

    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      cancelAnimationFrame(raf);
    };
  }, [threshold]);

  return progress;
}

/**
 * Clases "glass" reutilizables (solo para el sidebar de escritorio).
 */
const GLASS_SURFACE =
  'bg-white/80 dark:bg-black/90 ' +
  'supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-black/70 ' +
  'supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:backdrop-saturate-150';

/**
 * Liquid Glass real, estilo iOS 26 / WhatsApp iOS / InfoJobs, igual que en
 * SidebarClient. Nota: NO lleva la clase "relative" — Tailwind genera
 * .relative después de .fixed en su hoja de estilos, así que si conviven
 * en el mismo elemento, "relative" gana y el nav deja de estar fijo.
 */
const LIQUID_GLASS_BAR =
  'isolate overflow-hidden ' +
  'bg-white/85 dark:bg-[#1c1c1e]/90 ' +
  'supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-[#1c1c1e]/70 ' +
  'supports-[backdrop-filter]:backdrop-blur-[38px] supports-[backdrop-filter]:backdrop-saturate-[180%] ' +
  'border border-black/[0.08] dark:border-white/[0.12] ' +
  'shadow-[0_8px_32px_rgba(16,50,84,0.16),0_2px_8px_rgba(16,50,84,0.10),inset_0_1px_0_rgba(255,255,255,0.5),inset_0_-1px_0_rgba(255,255,255,0.06)] ' +
  'dark:shadow-[0_8px_32px_rgba(0,0,0,0.55),0_2px_8px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.14),inset_0_-1px_0_rgba(0,0,0,0.3)] ' +
  'before:content-[""] before:absolute before:inset-0 before:pointer-events-none before:rounded-[inherit] ' +
  'before:bg-[radial-gradient(120%_60%_at_50%_0%,rgba(255,255,255,0.55),rgba(255,255,255,0)_60%)] ' +
  'dark:before:bg-[radial-gradient(120%_60%_at_50%_0%,rgba(255,255,255,0.16),rgba(255,255,255,0)_60%)] ' +
  'after:content-[""] after:absolute after:inset-0 after:pointer-events-none after:rounded-[inherit] ' +
  'after:bg-[linear-gradient(115deg,rgba(255,255,255,0.35)_0%,rgba(255,255,255,0)_28%,rgba(255,255,255,0)_72%,rgba(255,255,255,0.18)_100%)] ' +
  'dark:after:bg-[linear-gradient(115deg,rgba(255,255,255,0.10)_0%,rgba(255,255,255,0)_28%,rgba(255,255,255,0)_72%,rgba(255,255,255,0.05)_100%)]';

/**
 * Cápsula de cristal activa: gota de vidrio con blur, brillo, borde y
 * reflejo, igual que la de SidebarClient.
 */
const GLASS_PILL =
  'absolute inset-0 -m-1.5 rounded-[22px] pointer-events-none isolate overflow-hidden ' +
  'bg-white/[0.55] dark:bg-white/[0.10] ' +
  'supports-[backdrop-filter]:backdrop-blur-md supports-[backdrop-filter]:backdrop-saturate-150 ' +
  'border border-white/[0.65] dark:border-white/[0.16] ' +
  'shadow-[0_4px_14px_rgba(16,50,84,0.20),0_1px_3px_rgba(16,50,84,0.12),inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_2px_rgba(16,50,84,0.10)] ' +
  'dark:shadow-[0_4px_14px_rgba(0,0,0,0.5),0_1px_3px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-1px_2px_rgba(0,0,0,0.35)] ' +
  'before:content-[""] before:absolute before:-inset-1 before:rounded-[24px] ' +
  'before:bg-[radial-gradient(80%_70%_at_50%_0%,rgba(255,255,255,0.9),rgba(255,255,255,0)_65%)] ' +
  'dark:before:bg-[radial-gradient(80%_70%_at_50%_0%,rgba(255,255,255,0.28),rgba(255,255,255,0)_65%)]';

interface CompanyAvatarProps {
  logoSrc: string | null | undefined;
  esDiamante: boolean;
  esPremium: boolean;
}

const CompanyAvatar = ({
  logoSrc,
  esDiamante,
  esPremium,
}: CompanyAvatarProps) => (
  <div className="relative shrink-0">
    <div className="w-10 h-10 rounded-full border border-white/40 dark:border-white/10 overflow-hidden flex items-center justify-center bg-white/70 dark:bg-gray-800/70 shadow-sm">
      {logoSrc ? (
        <img
          src={logoSrc}
          alt="Logo"
          className="object-cover w-full h-full"
        />
      ) : (
        <Building className="w-5 h-5 text-gray-400" />
      )}
    </div>
    {esPremium && (
      <span
        className="absolute -bottom-1 -right-1 w-5 h-5 flex items-center justify-center z-10 bg-white dark:bg-gray-800 rounded-full shadow-sm"
        style={{ fontSize: '12px' }}
        title={esDiamante ? 'Fundador Diamante' : 'Fundador Platino'}
      >
        {esDiamante ? '💎' : '🪙'}
      </span>
    )}
  </div>
);

const Sidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const empresaId = localStorage.getItem('id_empresa') || undefined;

  const { empresa, loadingEmpresa } = useEmpresa(empresaId);
  const { perfiles, loading: loadingProfiles } = useProfiles();
  const { theme, setTheme } = useTheme();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [ripples, setRipples] = useState<{ id: number; x: number; y: number }[]>([]);

  const location = useLocation();
  const currentPath = location.pathname;
  const scrollProgress = useScrollProgress(currentPath, mobileOpen);
  const suppressClicksUntilRef = useRef(0);

  // Logo recién subido desde el perfil (evento de useUploadEmpresaLogo).
  const [logoActualizado, setLogoActualizado] = useState<string | null>(null);
  useEffect(() => {
    const alCambiarLogo = (e: Event) =>
      setLogoActualizado((e as CustomEvent<string>).detail);
    window.addEventListener('empresa:logo', alCambiarLogo);
    return () => window.removeEventListener('empresa:logo', alCambiarLogo);
  }, []);
  const logoSrc = logoActualizado || empresa?.logo || null;
  const esDiamante = empresa?.es_fundador_diamante === true;
  const esPlatino = empresa?.es_fundador_platino === true;
  const esPremium = esDiamante || esPlatino;

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const toggleCollapsed = () => {
    setCollapsed((v) => !v);
    setTimeout(() => window.dispatchEvent(new Event('fc-resize')), 320);
  };

  const toggleMenu = (label: string) =>
    setOpenMenus((p) => ({ ...p, [label]: !p[label] }));

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  const handleChangeProfile = () => {
    navigate('/profiles');
    setMobileOpen(false);
  };

  // Genera una onda de agua en el punto donde se tocó el tab bar inferior.
  const spawnRipple = (
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement> | React.PointerEvent<HTMLElement>
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const point = 'touches' in e ? e.touches[0] : e;
    if (!point) return;
    const x = point.clientX - rect.left;
    const y = point.clientY - rect.top;
    const id = Date.now() + Math.random();
    setRipples((prev) => [...prev, { id, x, y }]);
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 650);
  };

  useEffect(() => {
    const onFullscreen = () => {
      setCollapsed(!!document.fullscreenElement);
      setTimeout(() => window.dispatchEvent(new Event('fc-resize')), 320);
    };
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => document.removeEventListener('fullscreenchange', onFullscreen);
  }, []);

  const navItems = [
    {
      label: t('sidebar.nav.dashboard'),
      to: '/panel-empresa',
      icon: <Home className="w-5 h-5" />,
      end: true,
    },
    {
      label: t('sidebar.nav.company'),
      icon: <Building className="w-5 h-5" />,
      children: [
        {
          label: t('sidebar.nav.profile'),
          to: '/panel-empresa/companyProfile',
          icon: <UserCog className="w-5 h-5" />,
        },
        {
          label: t('sidebar.nav.settings'),
          to: '/panel-empresa/companySettings',
          icon: <Settings className="w-5 h-5" />,
        },
        {
          label: t('sidebar.nav.expenses'),
          to: '/panel-empresa/expenses',
          icon: <Wallet className="w-5 h-5" />,
        },
        {
          label: t('sidebar.nav.subscriptions'),
          to: '/panel-empresa/subscriptions',
          icon: <CreditCard className="w-5 h-5" />,
        },
        {
          label: t('sidebar.nav.services'),
          icon: <Box className="w-5 h-5" />,
          children: [
            {
              label: t('sidebar.nav.servicesList'),
              to: '/panel-empresa/services',
              icon: <Box className="w-5 h-5" />,
            },
            {
              label: t('sidebar.nav.bonuses'),
              to: '/panel-empresa/bonus',
              icon: <Ticket className="w-5 h-5" />,
            },
            {
              label: t('sidebar.nav.products'),
              to: '/panel-empresa/products',
              icon: <Box className="w-5 h-5" />,
            },
          ],
        },
        {
          label: t('sidebar.nav.workers'),
          icon: <UsersIcon className="w-5 h-5" />,
          children: [
            {
              label: t('sidebar.nav.workersList'),
              to: '/panel-empresa/workers',
              icon: <UsersIcon className="w-5 h-5" />,
            },
            {
              label: t('sidebar.nav.absences'),
              to: '/panel-empresa/absences',
              icon: <Sun className="w-5 h-5" />,
            },
          ],
        },
      ],
    },
    {
      label: t('sidebar.nav.clients'),
      icon: <UsersIcon className="w-5 h-5" />,
      children: [
        {
          label: t('sidebar.nav.clientsList'),
          to: '/panel-empresa/clients',
          icon: <UsersIcon className="w-5 h-5" />,
        },
        {
          label: t('sidebar.nav.purchasedVouchers'),
          to: '/panel-empresa/purchasedVouchers',
          icon: <Tickets className="w-5 h-5" />,
        },
        {
          label: t('sidebar.nav.blacklist'),
          to: '/panel-empresa/blacklist',
          icon: <Shield className="w-5 h-5" />,
        },
      ],
    },
    {
      label: t('sidebar.nav.calendar'),
      to: '/panel-empresa/calendar',
      icon: <Calendar className="w-5 h-5" />,
      highlight: true,
    },
    {
      label: t('sidebar.nav.appointments'),
      to: '/panel-empresa/appointments',
      icon: <ClipboardList className="w-5 h-5" />,
    },
    {
      label: t('sidebar.nav.webBranding'),
      to: '/panel-empresa/branding',
      icon: <ScanLine className="w-5 h-5" />,
    },
  ];

  // Devuelve las labels de todos los padres que contienen la ruta actual,
  // para abrir automáticamente los submenús del item activo.
  const getActiveParentLabels = useMemo(() => {
    const matches: string[] = [];
    const walk = (items: any[], parents: string[]) => {
      for (const item of items) {
        if (item.to && currentPath.startsWith(item.to) && item.to !== '/') {
          matches.push(...parents);
        }
        if (item.children) {
          walk(item.children, [...parents, item.label]);
        }
      }
    };
    walk(navItems, []);
    return matches;
  }, [currentPath, t]);

  useEffect(() => {
    if (getActiveParentLabels.length === 0) return;
    setOpenMenus((prev) => {
      const next = { ...prev };
      for (const label of getActiveParentLabels) next[label] = true;
      return next;
    });
  }, [getActiveParentLabels]);

  const renderNavItem = (item: any) => {
    if (item.children) {
      return (
        <div key={item.label}>
          <button
            onClick={() => toggleMenu(item.label)}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl w-full transition
              ${
                openMenus[item.label]
                  ? 'bg-white/50 dark:bg-white/10 text-[#103254] dark:text-white shadow-sm'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-white/40 dark:hover:bg-white/5'
              }`}
          >
            {item.icon}
            {!collapsed && (
              <span className="flex-1 text-left truncate">{item.label}</span>
            )}
            {!collapsed && (
              <motion.div
                variants={chevronVariants}
                animate={openMenus[item.label] ? 'open' : 'closed'}
              >
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </motion.div>
            )}
          </button>

          <AnimatePresence>
            {openMenus[item.label] && !collapsed && (
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={submenuVariants}
                className="overflow-hidden ml-3 mt-1 pl-3 border-l border-gray-200/70 dark:border-white/10 flex flex-col gap-1"
              >
                {item.children.map((c: any) => renderNavItem(c))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <NavLink
        key={item.label}
        to={item.to}
        end={item.end}
        onClick={() => setMobileOpen(false)}
        className={({ isActive }) =>
          `flex items-center gap-2 px-3 py-2 text-sm rounded-xl transition
          ${
            isActive
              ? 'bg-[#103254] text-white shadow-lg shadow-[#103254]/30'
              : item.highlight
              ? 'text-blue-800 dark:text-blue-300 font-medium hover:bg-blue-100/60 dark:hover:bg-blue-900/30'
              : 'text-gray-600 dark:text-gray-400 hover:bg-white/40 dark:hover:bg-white/5'
          }`
        }
      >
        <span className="shrink-0">{item.icon}</span>
        {!collapsed && <span className="truncate">{item.label}</span>}
      </NavLink>
    );
  };

  const footer = (
    <div className="pt-4 border-t border-gray-200/70 dark:border-white/10 flex flex-col gap-1 shrink-0">
      <div
        className={`flex items-center gap-2 px-2 py-2 text-sm rounded-xl transition text-gray-700 dark:text-gray-300 hover:bg-white/40 dark:hover:bg-white/5 ${
          collapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        <span className="flex items-center gap-2 min-w-0">
          {theme === 'dark' ? (
            <Sun className="shrink-0 w-5 h-5" />
          ) : (
            <Moon className="shrink-0 w-5 h-5" />
          )}
          {!collapsed && (
            <span className="truncate text-sm">
              {theme === 'dark' ? t('sidebar.lightMode') : t('sidebar.darkMode')}
            </span>
          )}
        </span>
        {!collapsed && (
          <Switch
            checked={theme === 'dark'}
            onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
          />
        )}
      </div>

      {!loadingProfiles && perfiles.length > 1 && (
        <button
          onClick={handleChangeProfile}
          className="flex items-center gap-2 px-2 py-2 text-sm rounded-xl transition text-gray-700 dark:text-gray-300 hover:bg-white/40 hover:text-[#103254] dark:hover:bg-white/5 dark:hover:text-white"
        >
          <UsersIcon className="shrink-0 w-5 h-5" />
          {!collapsed && (
            <span className="truncate text-sm">
              {t('sidebar.changeProfile')}
            </span>
          )}
        </button>
      )}

      <button
        onClick={handleLogout}
        className="flex items-center gap-2 px-2 py-2 text-sm rounded-xl transition text-red-600 hover:bg-red-50/70 dark:hover:bg-red-900/20"
      >
        <LogOut className="shrink-0 w-5 h-5" />
        {!collapsed && (
          <span className="truncate text-sm">{t('sidebar.logout')}</span>
        )}
      </button>
    </div>
  );

  const sidebarContent = (
    <motion.div
      className="flex flex-col h-full p-4 overflow-hidden"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div
        variants={itemVariants}
        className={`flex items-center gap-2 p-3 mb-4 rounded-2xl shrink-0 ${
          collapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        {!collapsed && (
          <div
            className="flex items-center gap-3 min-w-0 cursor-pointer flex-1 overflow-hidden"
            onClick={() => {
              navigate('/panel-empresa');
              setMobileOpen(false);
            }}
          >
            {loadingEmpresa ? (
              <div className="w-10 h-10 rounded-full bg-gray-300 animate-pulse shrink-0" />
            ) : (
              <CompanyAvatar
                logoSrc={logoSrc}
                esDiamante={esDiamante}
                esPremium={esPremium}
              />
            )}

            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="font-medium text-gray-900 dark:text-white truncate">
                  {empresa?.empresa}
                </span>
                {esPremium && (
                  <span
                    className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full
                    ${
                      esDiamante
                        ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {esDiamante ? '💎' : '🪙'}
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('sidebar.panelLabel')}
              </span>
            </div>
          </div>
        )}

        {collapsed && (
          <div
            className="cursor-pointer mx-auto"
            onClick={() => navigate('/panel-empresa')}
          >
            {loadingEmpresa ? (
              <div className="w-10 h-10 rounded-full bg-gray-300 animate-pulse" />
            ) : (
              <CompanyAvatar
                logoSrc={logoSrc}
                esDiamante={esDiamante}
                esPremium={esPremium}
              />
            )}
          </div>
        )}

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={toggleCollapsed}
            className="shrink-0 p-1.5 rounded-xl hover:bg-white/50 dark:hover:bg-white/10 transition hidden lg:flex"
          >
            {collapsed ? (
              <ChevronRight className="w-5 h-5 text-[#103254]" />
            ) : (
              <ChevronLeft className="w-5 h-5 text-[#103254]" />
            )}
          </button>

          <button
            onClick={() => setMobileOpen(false)}
            className="shrink-0 p-1.5 rounded-xl hover:bg-white/50 dark:hover:bg-white/10 transition lg:hidden"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </motion.div>

      <div className="mx-3 mb-4 border-b border-gray-200/70 dark:border-white/10 shrink-0" />

      <nav className="flex flex-col gap-1 overflow-y-auto overflow-x-hidden flex-1 min-h-0">
        {navItems.map((i) => renderNavItem(i))}
      </nav>

      {footer}
    </motion.div>
  );

  // ── Menú móvil estilo "Ajustes de iOS" ──────────────────────────────────
  const iconCls = 'w-[18px] h-[18px] text-white';

  type MenuRow = {
    label: string;
    to?: string;
    icon: React.ReactNode;
    onClick?: () => void;
    danger?: boolean;
    children?: { label: string; to: string; icon: React.ReactNode }[];
  };

  const menuSections: { header?: string; items: MenuRow[] }[] = [
    {
      items: [
        {
          label: t('sidebar.nav.dashboard'),
          to: '/panel-empresa',
          icon: <Home className={iconCls} />,
        },
        {
          label: t('sidebar.nav.calendar'),
          to: '/panel-empresa/calendar',
          icon: <Calendar className={iconCls} />,
        },
        {
          label: t('sidebar.nav.appointments'),
          to: '/panel-empresa/appointments',
          icon: <ClipboardList className={iconCls} />,
        },
      ],
    },
    {
      header: t('sidebar.nav.company'),
      items: [
        {
          label: t('sidebar.nav.profile'),
          to: '/panel-empresa/companyProfile',
          icon: <UserCog className={iconCls} />,
        },
        {
          label: t('sidebar.nav.settings'),
          to: '/panel-empresa/companySettings',
          icon: <Settings className={iconCls} />,
        },
        {
          label: t('sidebar.nav.expenses'),
          to: '/panel-empresa/expenses',
          icon: <Wallet className={iconCls} />,
        },
        {
          label: t('sidebar.nav.subscriptions'),
          to: '/panel-empresa/subscriptions',
          icon: <CreditCard className={iconCls} />,
        },
        {
          label: t('sidebar.nav.services'),
          icon: <Box className={iconCls} />,
          children: [
            {
              label: t('sidebar.nav.servicesList'),
              to: '/panel-empresa/services',
              icon: <Box className={iconCls} />,
            },
            {
              label: t('sidebar.nav.bonuses'),
              to: '/panel-empresa/bonus',
              icon: <Ticket className={iconCls} />,
            },
            {
              label: t('sidebar.nav.products'),
              to: '/panel-empresa/products',
              icon: <Box className={iconCls} />,
            },
          ],
        },
        {
          label: t('sidebar.nav.workers'),
          icon: <UsersIcon className={iconCls} />,
          children: [
            {
              label: t('sidebar.nav.workersList'),
              to: '/panel-empresa/workers',
              icon: <UsersIcon className={iconCls} />,
            },
            {
              label: t('sidebar.nav.absences'),
              to: '/panel-empresa/absences',
              icon: <Sun className={iconCls} />,
            },
          ],
        },
      ],
    },
    {
      header: t('sidebar.nav.clients'),
      items: [
        {
          label: t('sidebar.nav.clientsList'),
          to: '/panel-empresa/clients',
          icon: <UsersIcon className={iconCls} />,
        },
        {
          label: t('sidebar.nav.purchasedVouchers'),
          to: '/panel-empresa/purchasedVouchers',
          icon: <Tickets className={iconCls} />,
        },
        {
          label: t('sidebar.nav.blacklist'),
          to: '/panel-empresa/blacklist',
          icon: <Shield className={iconCls} />,
        },
      ],
    },
    {
      header: t('sidebar.nav.webBranding'),
      items: [
        {
          label: t('sidebar.nav.webBranding'),
          to: '/panel-empresa/branding',
          icon: <ScanLine className={iconCls} />,
        },
      ],
    },
    {
      items: [
        {
          label:
            theme === 'dark' ? t('sidebar.lightMode') : t('sidebar.darkMode'),
          icon:
            theme === 'dark' ? (
              <Sun className={iconCls} />
            ) : (
              <Moon className={iconCls} />
            ),
          onClick: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
        },
        ...(!loadingProfiles && perfiles.length > 1
          ? [
              {
                label: t('sidebar.changeProfile'),
                icon: <UsersIcon className={iconCls} />,
                onClick: handleChangeProfile,
              },
            ]
          : []),
        {
          label: t('sidebar.logout'),
          icon: <LogOut className={iconCls} />,
          onClick: handleLogout,
          danger: true,
        },
      ],
    },
  ];

  const Squircle = ({ children }: { children: React.ReactNode }) => (
    <span
      className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center shrink-0"
      style={{
        background: 'linear-gradient(135deg, #2170b8 0%, #103254 100%)',
      }}
    >
      {children}
    </span>
  );

  const mobileMenuContent = (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Cabecera fija: título/cerrar + tarjeta de empresa. No hace scroll. */}
      <div className="shrink-0 px-4 pt-4 bg-[#f2f2f7] dark:bg-black">
        <div className="flex items-center justify-between mb-4 px-1">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('sidebar.menu')}
          </h1>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMobileOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200/70 dark:bg-white/10 text-gray-500 dark:text-gray-300"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <button
          onClick={() => {
            navigate('/panel-empresa/companyProfile');
            setMobileOpen(false);
          }}
          className="flex items-center gap-3 w-full rounded-2xl bg-white dark:bg-white/5 p-3 mb-4 text-left shadow-sm"
        >
          {loadingEmpresa ? (
            <div className="w-11 h-11 rounded-full bg-gray-300 animate-pulse shrink-0" />
          ) : (
            <CompanyAvatar
              logoSrc={logoSrc}
              esDiamante={esDiamante}
              esPremium={esPremium}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 dark:text-white truncate">
              {empresa?.empresa}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {t('sidebar.panelLabel')}
            </div>
          </div>
        </button>
      </div>

      {/* Contenido con scroll: solo las secciones de navegación */}
      <div
        className="flex-1 min-h-0 overflow-y-auto px-4"
        style={{ paddingBottom: 'calc(6rem + env(safe-area-inset-bottom))' }}
      >
      {menuSections.map((section, si) => (
        <div key={si} className="mb-6">
          {section.header && (
            <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 px-4 pb-1.5">
              {section.header}
            </h2>
          )}
          <div className="rounded-2xl overflow-hidden bg-white dark:bg-white/5 shadow-sm">
            {section.items.map((item, ii) => {
              const notLast = ii < section.items.length - 1;
              const sep = notLast
                ? 'border-b border-gray-200/70 dark:border-white/10'
                : '';
              const rowCls = `flex items-center gap-3 px-3 py-2.5 w-full text-left ${sep} active:bg-gray-100 dark:active:bg-white/5 transition-colors`;

              if (item.children) {
                const open = !!openMenus[item.label];
                return (
                  <div key={ii} className={notLast ? sep : ''}>
                    <button
                      onClick={() => toggleMenu(item.label)}
                      className="flex items-center gap-3 px-3 py-2.5 w-full text-left active:bg-gray-100 dark:active:bg-white/5 transition-colors"
                    >
                      <Squircle>{item.icon}</Squircle>
                      <span className="flex-1 text-[16px] text-gray-900 dark:text-white">
                        {item.label}
                      </span>
                      <motion.div
                        variants={chevronVariants}
                        animate={open ? 'open' : 'closed'}
                      >
                        <ChevronRight className="w-[18px] h-[18px] text-gray-300 dark:text-gray-600 shrink-0" />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {open && (
                        <motion.div
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          variants={submenuVariants}
                          className="overflow-hidden bg-gray-50 dark:bg-white/[0.03]"
                        >
                          {item.children.map((c, ci) => (
                            <NavLink
                              key={ci}
                              to={c.to}
                              onClick={() => setMobileOpen(false)}
                              className="flex items-center gap-3 pl-12 pr-3 py-2.5 w-full text-left active:bg-gray-100 dark:active:bg-white/5 transition-colors"
                            >
                              <span className="flex-1 text-[15px] text-gray-700 dark:text-gray-300">
                                {c.label}
                              </span>
                            </NavLink>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              const rowInner = (
                <>
                  <Squircle>{item.icon}</Squircle>
                  <span
                    className={`flex-1 text-[16px] ${
                      item.danger
                        ? 'text-red-500'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {item.label}
                  </span>
                </>
              );

              if (item.label === (theme === 'dark' ? t('sidebar.lightMode') : t('sidebar.darkMode'))) {
                return (
                  <div key={ii} className={rowCls}>
                    <Squircle>{item.icon}</Squircle>
                    <span className="flex-1 text-[16px] text-gray-900 dark:text-white">
                      {item.label}
                    </span>
                    <Switch
                      checked={theme === 'dark'}
                      onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                    />
                  </div>
                );
              }

              return item.to ? (
                <NavLink
                  key={ii}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={rowCls}
                >
                  {rowInner}
                </NavLink>
              ) : (
                <button key={ii} onClick={item.onClick} className={rowCls}>
                  {rowInner}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      </div>
    </div>
  );

  // ── Tab bar inferior estilo "líquido" (InfoJobs-like) ───────────────────
  // Pulsas cualquier icono, arrastras sin soltar por encima de los demás, y el
  // que tienes debajo del dedo se va "rellenando" con un blob que le sigue.
  // Al soltar, navegas directamente al que esté activo en ese momento.
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // 5 iconos en la tab bar móvil: Inicio, Citas, Calendario (centro), Web y Menú.
  const bottomTabs = useMemo(
    () => [
      {
        to: '/panel-empresa',
        end: true,
        icon: Home,
        label: t('sidebar.nav.dashboard'),
      },
      {
        to: '/panel-empresa/appointments',
        end: false,
        icon: ClipboardList,
        label: t('sidebar.nav.appointments'),
      },
      {
        to: '/panel-empresa/calendar',
        end: false,
        icon: Calendar,
        label: t('sidebar.nav.calendar'),
      },
      {
        to: '/panel-empresa/branding',
        end: false,
        icon: ScanLine,
        label: t('sidebar.nav.webBranding'),
      },
      {
        icon: mobileOpen ? X : Menu,
        label: t('sidebar.menu'),
        action: () => {
          suppressClicksUntilRef.current = Date.now() + 400;
          setMobileOpen((v) => !v);
        },
      },
    ],
    [t, mobileOpen]
  );

  const currentTabIndex = useMemo(() => {
    if (mobileOpen) return bottomTabs.length - 1;
    const idx = bottomTabs.findIndex(
      (tb) =>
        tb.to && (tb.end ? currentPath === tb.to : currentPath.startsWith(tb.to))
    );
    return idx === -1 ? null : idx;
  }, [bottomTabs, currentPath, mobileOpen]);

  const highlightIndex = isDragging ? dragIndex : currentTabIndex;

  const getIndexFromX = (x: number) => {
    let closest = 0;
    let closestDist = Infinity;
    tabRefs.current.forEach((el, i) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const dist = Math.abs(x - center);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    });
    return closest;
  };

  const handleTabPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    spawnRipple(e);
    try {
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      // Algunos navegadores pueden lanzar si el target no soporta capture; se ignora.
    }
    setIsDragging(true);
    setDragIndex(getIndexFromX(e.clientX));
  };

  const handleTabPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    if (!isDragging) return;
    setDragIndex(getIndexFromX(e.clientX));
  };

  const handleTabPointerUp = () => {
    if (dragIndex !== null) {
      const tab = bottomTabs[dragIndex];
      if (tab.to) {
        if (!guardNavigate(tab.to)) {
          navigate(tab.to);
          setMobileOpen(false);
        }
      } else if (tab.action) {
        tab.action();
      }
    }
    setIsDragging(false);
    setDragIndex(null);
  };

  const tabbarPadding = 0.25 - 0.1 * scrollProgress;
  const tabbarBottomGap = 0.6 - 0.12 * scrollProgress;
  const tabbarSideMargin = 1 + 0.5 * scrollProgress; // rem — crece al hacer scroll, estrechando la barra

  return (
    <>
      {/* Campanita de notificaciones + botón de ayuda: fijos globales
          (arriba a la derecha) — una sola instancia para toda la pantalla,
          no una por breakpoint. */}
      <NotificationBell />
      <HelpButton />

      {/* Tab bar inferior Liquid Glass: flotante, cristal real con blur y
          reflejo, con gesto de arrastre magnético + ondas de agua al pulsar.
          Se oculta mientras el menú móvil a pantalla completa está abierto. */}
      <nav
        onPointerDown={handleTabPointerDown}
        onPointerMove={handleTabPointerMove}
        onPointerUp={handleTabPointerUp}
        onPointerCancel={handleTabPointerUp}
        className={`fixed z-50 lg:hidden flex items-center justify-around rounded-[34px] transition-[padding,bottom,opacity,transform,left,right] duration-300 ease-out select-none ${LIQUID_GLASS_BAR}
          ${mobileOpen ? 'opacity-0 pointer-events-none translate-y-2' : 'opacity-100'}`}
        style={{
          left: `${tabbarSideMargin}rem`,
          right: `${tabbarSideMargin}rem`,
          bottom: `calc(${tabbarBottomGap}rem + env(safe-area-inset-bottom))`,
          paddingTop: `${tabbarPadding}rem`,
          paddingBottom: `${tabbarPadding}rem`,
          paddingLeft: '0.4rem',
          paddingRight: '0.4rem',
          touchAction: 'none',
        }}
      >
        {/* Ondas de agua al pulsar */}
        <AnimatePresence>
          {ripples.map((r) => (
            <motion.span
              key={r.id}
              className="absolute rounded-full pointer-events-none z-0"
              style={{
                left: r.x,
                top: r.y,
                translateX: '-50%',
                translateY: '-50%',
                background:
                  'radial-gradient(circle, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,0) 70%)',
              }}
              initial={{ width: 0, height: 0, opacity: 0.9 }}
              animate={{ width: 260, height: 260, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.65, ease: 'easeOut' }}
            />
          ))}
        </AnimatePresence>

        {bottomTabs.map((tab, i) => {
          const active = highlightIndex === i;
          const Icon = tab.icon;
          const p = scrollProgress;
          const iconSize = 1.15 - 0.12 * p;

          return (
            <div
              key={tab.label}
              ref={(el) => (tabRefs.current[i] = el)}
              className="relative z-10 flex justify-center items-center flex-1 py-1"
              aria-label={tab.label}
            >
              <motion.div
                className="relative z-10 flex flex-col justify-center items-center gap-0.5 px-2 py-1"
                animate={{ scale: active ? 1.08 : 1 }}
                transition={{ type: 'spring', stiffness: 320, damping: 28, mass: 0.7 }}
              >
                {active && (
                  <motion.span
                    layoutId="tab-indicator-empresa"
                    className={GLASS_PILL}
                    transition={{ type: 'spring', stiffness: 300, damping: 27, mass: 0.7 }}
                  />
                )}

                <Icon
                  className={`relative z-10 transition-colors duration-200 ${
                    active
                      ? 'text-[#0d1f33] dark:text-white'
                      : 'text-gray-700 dark:text-white/60'
                  }`}
                  style={{ width: `${iconSize}rem`, height: `${iconSize}rem` }}
                  strokeWidth={active ? 2.2 : 1.75}
                />

                <span
                  className={`relative z-10 text-[10px] leading-none font-medium whitespace-nowrap transition-colors duration-200 ${
                    active
                      ? 'text-[#0d1f33] dark:text-white'
                      : 'text-gray-700 dark:text-white/60'
                  }`}
                >
                  {tab.label}
                </span>
              </motion.div>
            </div>
          );
        })}
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.aside
            className="fixed inset-0 z-40 w-full bg-[#f2f2f7] dark:bg-black flex flex-col lg:hidden overflow-hidden"
            onClickCapture={(e) => {
              if (Date.now() < suppressClicksUntilRef.current) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            style={{
              paddingTop: 'env(safe-area-inset-top)',
            }}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
          >
            {mobileMenuContent}
          </motion.aside>
        )}
      </AnimatePresence>

      <aside
        className={`
          hidden lg:flex lg:flex-col lg:min-h-screen
          ${GLASS_SURFACE}
          border-r border-white/40 dark:border-white/10
          shadow-2xl transition-[width] duration-300 ease-in-out overflow-x-hidden
          ${collapsed ? 'w-20' : 'w-72'}
        `}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default memo(Sidebar);